import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppConfiguration } from 'src/app/app.configuration';
import {
  buildWayletExternosBody,
  readConexionIacpos,
  readWayletClienteApi,
  resolveWayletPasarelaEntorno,
  WayletExternosConfig,
  WayletExternosTipo,
} from 'src/app/core/utils/waylet-externos.util';
import {
  GenerarIdentificadorWayletRequest,
  WayletEstadoResponse,
  WayletGenerarIdentificadorResponse,
  WayletPagoResponse,
} from '../models/pago.model';

export interface WayletDevolucionResult {
  ok: boolean;
  refundedAmount?: number;
  mensaje?: string;
}

@Injectable({
  providedIn: 'root',
})
export class WayletService {
  constructor(private http: HttpClient, private config: AppConfiguration) {}

  resolveExternosConfig(): WayletExternosConfig | null {
    const clienteApi = readWayletClienteApi();
    if (!clienteApi) {
      return null;
    }
    return {
      conexionIacpos: readConexionIacpos(),
      empresaClave: this.config.getConfig('chClaveEmpresa'),
      proyectoNombre: this.config.getConfig('chNombreProyecto'),
      pasarelaEntorno: resolveWayletPasarelaEntorno(
        this.config.getConfig('chEntornoConexion')
      ),
      clienteApi,
    };
  }

  buildExternosBody(
    tipo: WayletExternosTipo,
    params: {
      paymentId: string;
      amountCentimos?: number;
      qrContent?: string;
      wayletPaymentId?: string;
    }
  ): Record<string, unknown> | null {
    const config = this.resolveExternosConfig();
    if (!config) {
      return null;
    }
    return buildWayletExternosBody(config, tipo, params);
  }

  generarIdentificadorWaylet(
    request: GenerarIdentificadorWayletRequest
  ): Observable<WayletGenerarIdentificadorResponse> {
    const url = `${this.config.getConfig('API_URL_MS')}/api/Operacion/GenerarIdentificadorWaylet`;
    console.log('[Waylet] HTTP GenerarIdentificadorWaylet', { url, request });
    const params = new HttpParams()
      .set('conexionId', this.getConexionId())
      .set('plataforma', String(request.plataforma ?? 2))
      .set('codigoValidacionUnico', request.codigoValidacionUnico)
      .set('tpvId', String(request.tpvId));

    return this.http
      .get<any>(url, { params })
      .pipe(map((res) => this.normalizeGenerarIdentificadorResponse(res)));
  }

  pagoWaylet(body: any): Observable<WayletPagoResponse> {
    const url = `${this.config.getConfig('API_URL')}api/Externos/PagoWaylet`;
    console.log('[Waylet] HTTP PagoWaylet', { url, body });
    return this.http
      .post<any>(url, body)
      .pipe(map((res) => this.normalizePagoResponse(res)));
  }

  getRespuestaWaylet(body: any): Observable<WayletEstadoResponse> {
    const payload = {
      conexionIacpos: body?.conexionIacpos ?? this.getConexionId(),
      paymentId: body?.paymentId ?? '',
      wayletPaymentId: body?.wayletPaymentId ?? '',
    };
    return this.http
      .post<any>(
        `${this.config.getConfig('API_URL')}api/Externos/GetRespuestaWaylet`,
        payload
      )
      .pipe(map((res) => this.normalizeEstadoResponse(res)));
  }

  cancelarPagoWaylet(body: any): Observable<boolean> {
    return this.http
      .post<any>(`${this.config.getConfig('API_URL')}api/Externos/CancelarPagoWaylet`, body)
      .pipe(map((res) => this.isRespuestaOk(res)));
  }

  devolverPagoWaylet(body: any): Observable<boolean> {
    return this.procesarDevolucionWayletFromBody(body).pipe(
      map((result) => result.ok)
    );
  }

  procesarDevolucionWaylet(
    paymentId: string,
    importeEuros: number
  ): Observable<WayletDevolucionResult> {
    const body = this.buildExternosBody('devolver', {
      paymentId,
      amountCentimos: Math.round(importeEuros * 100),
    });
    if (!body) {
      return of({
        ok: false,
        mensaje: 'No se pudo construir la petición Waylet (clienteApi o configuración)',
      });
    }
    return this.procesarDevolucionWayletFromBody(body);
  }

  private procesarDevolucionWayletFromBody(
    body: Record<string, unknown>
  ): Observable<WayletDevolucionResult> {
    const url = `${this.config.getConfig('API_URL')}api/Externos/DevolverPagoWaylet`;
    console.log('[Waylet] HTTP DevolverPagoWaylet', { url, body });
    return this.http.post<any>(url, body).pipe(
      map((res) => {
        if (!this.isRespuestaOk(res)) {
          const mensajes = res?.mensajes ?? res?.Mensajes ?? [];
          const mensaje =
            mensajes[0]?.descripcionMensaje ??
            mensajes[0]?.DescripcionMensaje ??
            'Error al devolver pago Waylet';
          return { ok: false, mensaje };
        }
        const datos = this.getDatosResult(res);
        const refundedAmount =
          datos?.refundedAmount ?? datos?.RefundedAmount ?? undefined;
        return { ok: true, refundedAmount };
      }),
      catchError(() =>
        of({ ok: false, mensaje: 'Error de comunicación con DevolverPagoWaylet' })
      )
    );
  }

  private getConexionId(): string {
    const raw = localStorage.getItem('conexionId');
    if (!raw) {
      return '';
    }
    try {
      return String(JSON.parse(raw)).replace(/[^0-9]/g, '');
    } catch {
      return raw.replace(/[^0-9]/g, '');
    }
  }

  private getDatosResult(res: any): any {
    return res?.datosResult ?? res?.DatosResult ?? null;
  }

  private isRespuestaOk(res: any): boolean {
    const datos = this.getDatosResult(res);
    return !!res && datos !== null && datos !== undefined;
  }

  private normalizePagoResponse(res: any): WayletPagoResponse {
    const datos = this.getDatosResult(res);
    return {
      DatosResult: {
        wayletPaymentId:
          datos?.wayletPaymentId ?? datos?.WayletPaymentId ?? '',
      },
      Mensajes: res?.mensajes ?? res?.Mensajes ?? [],
      LoginDatos: res?.loginDatos ?? res?.LoginDatos ?? null,
    };
  }

  private normalizeEstadoResponse(res: any): WayletEstadoResponse {
    const datos = this.getDatosResult(res);
    return {
      DatosResult: datos
        ? {
            paymentId: datos.paymentId ?? datos.PaymentId ?? '',
            wayletPaymentId:
              datos.wayletPaymentId ?? datos.WayletPaymentId ?? '',
            status: datos.status ?? datos.Status ?? '',
            errorCode: datos.errorCode ?? datos.ErrorCode ?? '',
            errorDescription:
              datos.errorDescription ?? datos.ErrorDescription ?? '',
            amount: datos.amount ?? datos.Amount ?? '',
            signature: datos.signature ?? datos.Signature ?? '',
          }
        : null,
      Mensajes: res?.mensajes ?? res?.Mensajes ?? [],
      LoginDatos: res?.loginDatos ?? res?.LoginDatos ?? null,
    };
  }

  private normalizeGenerarIdentificadorResponse(
    res: any
  ): WayletGenerarIdentificadorResponse {
    const datos = res?.datosResult ?? res?.DatosResult ?? {};
    const paymentId =
      datos.identificadorWaylet ??
      datos.IdentificadorWaylet ??
      datos.paymentId ??
      datos.PaymentId ??
      '';

    return {
      DatosResult: { paymentId },
      Mensajes: res?.mensajes ?? res?.Mensajes ?? [],
      LoginDatos: res?.loginDatos ?? res?.LoginDatos ?? null,
    };
  }
}
